import { useState } from 'react';
import { Button, Empty, Spin, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { showMoveError, showMoveSuccess } from './moveMessages';
import {
	useConfirmMoveMutation,
	useInitiateMoveMutation,
	useMovePairMutation,
	useListStagingAreasQuery,
	useListTrackedCardsQuery,
	TrackedCard,
} from '../../redux/api/kit-staging';
import { deriveCartStops, groupCartStopsByOli, CartStop } from './cartStops';
import { groupByOli, OliGroup } from '../../pages/KitStaging/boards/groupByOli';
import { OliCardTile, CardPairControl } from './OliCardTile';
import { MoveDrawer } from './MoveDrawer';

// Cart stop secondary-action type (kept for compatibility with handlers).
export type SecondaryAction = 'remove' | 'dekit' | 'machine' | 'override';

interface Props {
	onSelect?: (g: OliGroup | null) => void;
	selectedGroup?: OliGroup | null;
}

/** Compute the right-edge pair control for a cart OLI: a one-tap "Move to JSA"
 *  when the kit is ready and the material is ready OR still cutting (partial),
 *  with both cards on the cart; or a "Pending → JSA" state (with Undo) once the
 *  pair move has been initiated. Hidden otherwise. */
function cartPairControl(
	g: OliGroup,
	oli: string,
	runPair: (oli: string, action: 'initiate' | 'undo', okMsg: string) => void,
	loading: boolean,
): CardPairControl | undefined {
	const kit = g.kitCard;
	const mat = g.materialCard;
	if (!kit || !mat) return undefined;
	const blocked = [kit.status, mat.status].some((s) =>
		['site_mismatch', 'task_status_mismatch', 'cancelled', 'on_hold'].includes(s),
	);
	if (blocked) return undefined;
	const bothOnCart = kit.current_area === 'cart' && mat.current_area === 'cart';
	const pairPending = kit.pending_target_area === 'jsa' && mat.pending_target_area === 'jsa';
	if (pairPending) {
		return { mode: 'pending', onUndo: () => runPair(oli, 'undo', 'Pair returned to cart.'), loading };
	}
	// Show the shortcut when the kit is ready and the material is ready OR still
	// cutting (partial) — the pair can stage at JSA while the material finishes.
	const kitReady = !!g.job?.kit_ready;
	const materialReadyOrPartial = !!g.job?.material_ready || !!g.job?.material_partial;
	if (bothOnCart && kitReady && materialReadyOrPartial) {
		return { mode: 'move', onMove: () => runPair(oli, 'initiate', 'Moving pair to JSA.'), loading };
	}
	return undefined;
}

export function CartRouteView({ onSelect, selectedGroup }: Props) {
	const cardsQuery = useListTrackedCardsQuery({}, { pollingInterval: 10000 });
	const areasQuery = useListStagingAreasQuery(undefined, { pollingInterval: 10000 });
	const { data: cards, isLoading: cardsLoading, isFetching: cardsFetching } = cardsQuery;
	const { data: areas, isLoading: areasLoading } = areasQuery;
	const [initiateMove] = useInitiateMoveMutation();
	const [confirmMove] = useConfirmMoveMutation();
	const [movePair, { isLoading: pairLoading }] = useMovePairMutation();
	const [drawerCard, setDrawerCard] = useState<TrackedCard | null>(null);
	const onRefresh = () => {
		cardsQuery.refetch();
		areasQuery.refetch();
	};

	// One-tap pair move: kit + material → JSA together on one shelf.
	const runPair = async (oli: string, action: 'initiate' | 'undo', okMsg: string) => {
		try {
			await movePair({ order_line_item_id: oli, action }).unwrap();
			showMoveSuccess(okMsg);
		} catch (e) {
			showMoveError(e, "Couldn't move the pair.");
		}
	};

	if (cardsLoading || areasLoading) {
		return (
			<div style={{ padding: 48, textAlign: 'center' }}>
				<Spin />
			</div>
		);
	}

	const stops = deriveCartStops(cards ?? [], areas ?? []);
	const stopGroups = groupCartStopsByOli(stops);
	const groupsByOli = new Map(groupByOli(cards ?? []).map((g) => [g.oli, g]));

	if (stops.length === 0) {
		return (
			<div style={{ padding: 48 }}>
				<Empty description="No kits on cart" />
			</div>
		);
	}

	const onDeliver = async (stop: CartStop) => {
		if (!stop.destinationArea || !stop.destinationShelf) return;
		const dest = stop.destinationArea.area_type === 'job_staging' ? 'JSA' : 'MSA';
		try {
			await initiateMove({
				id: stop.card.id,
				target_area: stop.destinationArea.area_type === 'job_staging' ? 'jsa' : 'msa',
				target_shelf: stop.destinationShelf.id,
			}).unwrap();
			showMoveSuccess(`Delivering to ${dest} — tap CONFIRM to finalize.`);
		} catch (e) {
			showMoveError(e, `Couldn't start delivery to ${dest}.`);
		}
	};

	const onConfirm = async (stop: CartStop) => {
		try {
			await confirmMove({ id: stop.card.id }).unwrap();
			showMoveSuccess('Placement confirmed.');
		} catch (e) {
			showMoveError(e, "Couldn't confirm placement.");
		}
	};

	const onPickDestination = (stop: CartStop) => {
		setDrawerCard(stop.card);
	};

	const onSecondaryAction = async (stop: CartStop, action: SecondaryAction) => {
		if (action === 'remove') {
			try {
				await confirmMove({ id: stop.card.id, target_area: 'unassigned' }).unwrap();
				showMoveSuccess('Removed from cart.');
			} catch (e) {
				showMoveError(e, "Couldn't remove from cart.");
			}
		} else if (action === 'dekit') {
			try {
				await confirmMove({ id: stop.card.id, target_area: 'dekit' }).unwrap();
				showMoveSuccess('Sent to dekit.');
			} catch (e) {
				showMoveError(e, "Couldn't move to dekit.");
			}
		} else if (action === 'machine') {
			// CART → MACHINE is DUAL per Q3.1 + "both confirms in cart route view".
			// Initiate inline; the row re-renders pending and shows CONFIRM PLACEMENT for the second tap.
			try {
				await initiateMove({ id: stop.card.id, target_area: 'machine' }).unwrap();
				showMoveSuccess('Sending to machine — tap CONFIRM to finalize.');
			} catch (e) {
				showMoveError(e, "Couldn't start machine move.");
			}
		} else if (action === 'override') {
			setDrawerCard(stop.card);
		}
	};

	return (
		<div style={{ padding: 12, maxWidth: 720, marginLeft: 'auto', marginRight: 'auto' }}>
			<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
				<Typography.Text strong style={{ fontSize: 13 }}>
					{stopGroups.length} {stopGroups.length === 1 ? 'stop' : 'stops'}
				</Typography.Text>
				<Button icon={<ReloadOutlined spin={cardsFetching} />} size="small" onClick={onRefresh} aria-label="Refresh cart">
					Refresh
				</Button>
			</div>
			<div
				style={{
					marginTop: 8,
					display: 'flex',
					flexDirection: 'column',
					gap: 8,
				}}
			>
				{stopGroups.map((sg) => {
					const oliGroup = groupsByOli.get(sg.oli) ?? null;
					if (!oliGroup) return null;
					return (
						<OliCardTile
								key={sg.oli}
								group={oliGroup}
								currentArea="cart"
								selected={selectedGroup?.oli === sg.oli}
								onSelect={(g) => onSelect?.(g)}
								pair={cartPairControl(oliGroup, sg.oli, runPair, pairLoading)}
							/>
					);
				})}
			</div>
			{/* Suppress unused-handler lint by referencing them; the inline DELIVER
          path now lives in the sidebar's per-item action sections. */}
			{false && [onDeliver, onConfirm, onPickDestination, onSecondaryAction]}
			{drawerCard && <MoveDrawer open={!!drawerCard} card={drawerCard} onClose={() => setDrawerCard(null)} />}
		</div>
	);
}
